import React, { FC } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import type { Vendor } from '../core/_models'

const backdropStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.55)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2.5rem 1.25rem',
  overflowY: 'auto',
}

interface VendorPerformanceModalProps {
  visible: boolean
  vendor: Vendor | null
  onClose: () => void
}

const formatPercent = (val?: number | null): string => {
  if (val === undefined || val === null || !Number.isFinite(val)) return '-'
  return `${(val <= 1 ? val * 100 : val).toFixed(1)}%`
}

const formatNumber = (val?: number | null, fallback = '-'): string => {
  if (val === undefined || val === null || !Number.isFinite(val)) return fallback
  return String(val)
}

const VendorPerformanceModal: FC<VendorPerformanceModalProps> = ({ visible, vendor, onClose }) => {
  if (!visible || !vendor) return null

  const categoryPerformance = vendor.categoryPerformance || []

  return (
    <div style={backdropStyle} onClick={onClose}>
      <div
        className='card shadow-lg w-100 bg-white'
        style={{
          maxWidth: '1000px',
          maxHeight: 'calc(100vh - 4rem)',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '14px',
          overflow: 'hidden',
          margin: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className='card-header d-flex align-items-center justify-content-between px-6 py-4 border-bottom'
          style={{ flexShrink: 0, minHeight: 'unset' }}
        >
          <div>
            <h3 className='mb-1 fw-bold text-dark d-flex align-items-center gap-2'>
              <span>{vendor.name}</span>
              {vendor.code && <span className='badge badge-light-primary fs-7'>{vendor.code}</span>}
              {vendor.vendorInitials && (
                <span className='badge badge-light-info fs-7'>{vendor.vendorInitials}</span>
              )}
            </h3>
            <div className='text-muted fs-7'>Vendor Performance Metrics &amp; Delivery History</div>
          </div>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-light'
            style={{ borderRadius: '8px' }}
            onClick={onClose}
            aria-label='Close performance modal'
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
          </button>
        </div>

        <div className='card-body px-6 py-5' style={{ overflowY: 'auto', flexGrow: 1 }}>
          {/* Summary KPI Cards */}
          <div className='row g-4 mb-6'>
            <div className='col-md-3 col-6'>
              <div className='border rounded p-4 text-center bg-light'>
                <div className='text-muted small fw-semibold text-uppercase mb-1'>Overall Rating</div>
                <div className='fs-2 fw-bolder text-primary'>
                  {vendor.performanceRating != null
                    ? `${Number(vendor.performanceRating).toFixed(1)} / 5`
                    : 'N/A'}
                </div>
              </div>
            </div>

            <div className='col-md-3 col-6'>
              <div className='border rounded p-4 text-center bg-light'>
                <div className='text-muted small fw-semibold text-uppercase mb-1'>Total Orders</div>
                <div className='fs-2 fw-bolder text-dark'>{formatNumber(vendor.totalOrders, '0')}</div>
              </div>
            </div>

            <div className='col-md-3 col-6'>
              <div className='border rounded p-4 text-center bg-light'>
                <div className='text-muted small fw-semibold text-uppercase mb-1'>Delivered Records</div>
                <div className='fs-2 fw-bolder text-success'>{formatNumber(vendor.deliveredRecords, '0')}</div>
              </div>
            </div>

            <div className='col-md-3 col-6'>
              <div className='border rounded p-4 text-center bg-light'>
                <div className='text-muted small fw-semibold text-uppercase mb-1'>Avg Delivery Time</div>
                <div className='fs-2 fw-bolder text-info'>
                  {vendor.avgDeliveryDays != null ? `${vendor.avgDeliveryDays} days` : '-'}
                </div>
              </div>
            </div>
          </div>

          {/* Issues Indicator */}
          <div className='d-flex align-items-center justify-content-between p-3 mb-6 rounded border bg-light-warning'>
            <div className='d-flex align-items-center gap-2'>
              <i className='bi bi-exclamation-triangle-fill text-warning fs-4' />
              <span className='fw-semibold text-dark'>Reported Delivery Issues &amp; Discrepancies</span>
            </div>
            <span className='badge badge-warning fs-6 px-3 py-2'>
              {vendor.issueCount ?? 0} {vendor.issueCount === 1 ? 'issue' : 'issues'}
            </span>
          </div>

          {/* Category Breakdown Table */}
          <div className='mb-2'>
            <h5 className='fw-bold text-dark mb-3'>Performance by Category</h5>
            {categoryPerformance.length === 0 ? (
              <div className='text-muted text-center py-6 border rounded bg-light'>
                No category-specific performance records found for this vendor.
              </div>
            ) : (
              <div className='table-responsive border rounded'>
                <table className='table table-row-dashed table-row-gray-200 align-middle gs-4 gy-3 mb-0'>
                  <thead>
                    <tr className='text-muted fw-bold fs-7 text-uppercase bg-light'>
                      <th>Category</th>
                      <th className='text-center'>Orders</th>
                      <th className='text-center'>Avg Delivery</th>
                      <th className='text-center'>Delivered</th>
                      <th className='text-center'>Good</th>
                      <th className='text-center'>Damaged</th>
                      <th className='text-center'>Missing</th>
                      <th className='text-center'>Quality Rate</th>
                      <th className='text-center'>Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryPerformance.map((cat, idx) => (
                      <tr key={cat.categoryId ?? idx}>
                        <td className='fw-semibold text-dark'>{cat.categoryName}</td>
                        <td className='text-center'>{formatNumber(cat.totalCompletedOrders, '0')}</td>
                        <td className='text-center'>
                          {cat.averageDeliveryDays != null
                            ? `${Number(cat.averageDeliveryDays).toFixed(1)} d`
                            : '-'}
                        </td>
                        <td className='text-center'>{formatNumber(cat.deliveredItemCount, '0')}</td>
                        <td className='text-center text-success'>{formatNumber(cat.goodItemCount, '0')}</td>
                        <td className='text-center text-danger'>{formatNumber(cat.damagedItemCount, '0')}</td>
                        <td className='text-center text-warning'>{formatNumber(cat.missingItemCount, '0')}</td>
                        <td className='text-center fw-semibold text-primary'>
                          {formatPercent(cat.qualityRate)}
                        </td>
                        <td className='text-center fw-bold'>
                          {cat.categoryRating != null
                            ? `${Number(cat.categoryRating).toFixed(1)}/5`
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end px-6 py-4 border-top'>
          <button type='button' className='btn btn-light' onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default VendorPerformanceModal

